import type {
  PublicProfileAnalyticsComparison,
  PublicProfileAnalyticsRange,
  PublicProfileAnalyticsRecentActivity,
  PublicProfileAnalyticsSeriesPoint,
  PublicProfileAnalyticsSummary,
} from '@tabbio/types';

/**
 * Pure view model for the CV analytics quick look and full screen (the
 * 4 Oct 2026 Free/Premium handoff). Both platforms render from these
 * helpers so the numbers, chart and feed agree wherever they are drawn.
 */

export type CvAnalyticsMetric = 'views' | 'downloads' | 'shares';

export const CV_ANALYTICS_METRICS: readonly CvAnalyticsMetric[] = [
  'views',
  'downloads',
  'shares',
];

/** Filter order in the duration row: All, 7d, 30d, 90d. */
export const CV_ANALYTICS_RANGES: readonly PublicProfileAnalyticsRange[] = [
  'all',
  '7d',
  '30d',
  '90d',
];

const RANGE_DAYS: Record<Exclude<PublicProfileAnalyticsRange, 'all'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

/** Bars on the chart at most; longer windows group days into one bar. */
export const CV_ANALYTICS_MAX_BARS = 30;
/** Activity rows before "Show more" (Premium). */
export const CV_ANALYTICS_ACTIVITY_PAGE = 6;
/** Avatars in the quick look stack before the "+" chip. */
export const CV_ANALYTICS_STACK_SIZE = 3;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Locale-aware integer (grouping, Arabic-Indic digits where the locale uses them). */
export function formatCvAnalyticsCount(value: number, locale: string) {
  try {
    return new Intl.NumberFormat(locale).format(value);
  } catch {
    return String(value);
  }
}

function firstParam(value: string | string[] | null | undefined) {
  return Array.isArray(value) ? value[0] : value;
}

export function parseCvAnalyticsRange(
  value: string | string[] | null | undefined,
): PublicProfileAnalyticsRange {
  const raw = firstParam(value);
  return CV_ANALYTICS_RANGES.includes(raw as PublicProfileAnalyticsRange)
    ? (raw as PublicProfileAnalyticsRange)
    : 'all';
}

export function parseCvAnalyticsMetric(
  value: string | string[] | null | undefined,
): CvAnalyticsMetric {
  const raw = firstParam(value);
  return CV_ANALYTICS_METRICS.includes(raw as CvAnalyticsMetric)
    ? (raw as CvAnalyticsMetric)
    : 'views';
}

/**
 * The API's filter value for the Main CV: the public CV built from the
 * profile, whose events carry no CV id (see resolveAnalyticsCvWhere).
 */
export const MAIN_CV_ANALYTICS_SCOPE = 'main';

/**
 * A CV filter from a route param or CV target: a CV id, `main` for the Main
 * CV (the editor's `master` target), or null for every CV.
 */
export function parseCvAnalyticsCv(value: string | string[] | null | undefined) {
  const raw = firstParam(value)?.trim();
  // `grant:` / `access:` targets are someone else's CV opened by delegation:
  // not one of this account's CVs, so the page-wide numbers apply.
  if (!raw || raw === 'all' || raw.includes(':')) return null;
  return raw === 'master' ? MAIN_CV_ANALYTICS_SCOPE : raw;
}

export function metricTotal(
  totals: PublicProfileAnalyticsSummary['totals'] | null | undefined,
  metric: CvAnalyticsMetric,
) {
  const value = totals?.[metric];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export type CvAnalyticsTrend =
  | { kind: 'change'; direction: 'up' | 'down' | 'flat'; percent: number }
  | { kind: 'new' };

/**
 * The handoff's growth figure, from the server's two windows (see
 * PublicProfileAnalyticsComparison): round((now - before) / before x 100).
 * Nothing before and something now is "New"; nothing in either is 0%. Null
 * only when the API sent no comparison (older API or a failed read).
 */
export function buildCvAnalyticsTrend(
  comparison: PublicProfileAnalyticsComparison | null | undefined,
  metric: CvAnalyticsMetric,
): CvAnalyticsTrend | null {
  const now = comparison?.current?.[metric];
  const before = comparison?.previous?.[metric];
  if (typeof now !== 'number' || !Number.isFinite(now)) return null;
  if (typeof before !== 'number' || !Number.isFinite(before)) return null;
  if (before <= 0) {
    return now > 0 ? { kind: 'new' } : { kind: 'change', direction: 'flat', percent: 0 };
  }
  const percent = Math.round(((now - before) / before) * 100);
  return {
    kind: 'change',
    direction: percent > 0 ? 'up' : percent < 0 ? 'down' : 'flat',
    percent: Math.abs(percent),
  };
}

function utcDayKey(time: number) {
  return new Date(time).toISOString().slice(0, 10);
}

function startOfUtcDay(time: number) {
  const date = new Date(time);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export interface CvAnalyticsChart {
  /** One value per bar, oldest first. */
  bars: number[];
  /** Days per bar (1 up to 30 days, then grouped). */
  daysPerBar: number;
  /** UTC midnight of the first day on the chart. */
  startTime: number;
  /** Number of days covered. */
  days: number;
  max: number;
  total: number;
}

/**
 * Daily series → at most 30 bars ending today. All time starts at the first
 * day with data (at least 30 days, so a new account's chart is not one fat
 * bar). Days the API omitted are zero.
 */
export function buildCvAnalyticsChart(
  series: readonly PublicProfileAnalyticsSeriesPoint[] | null | undefined,
  metric: CvAnalyticsMetric,
  range: PublicProfileAnalyticsRange,
  now: number = Date.now(),
): CvAnalyticsChart {
  const today = startOfUtcDay(now);
  const byDay = new Map<string, number>();
  let firstDay = today;
  for (const point of series ?? []) {
    const value = point[metric];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    byDay.set(point.date, (byDay.get(point.date) ?? 0) + value);
    const time = Date.parse(`${point.date}T00:00:00.000Z`);
    if (Number.isFinite(time) && time < firstDay) firstDay = time;
  }

  const days =
    range === 'all'
      ? Math.max(CV_ANALYTICS_MAX_BARS, Math.round((today - firstDay) / DAY_MS) + 1)
      : RANGE_DAYS[range];
  const startTime = today - (days - 1) * DAY_MS;
  const daily = Array.from(
    { length: days },
    (_, index) => byDay.get(utcDayKey(startTime + index * DAY_MS)) ?? 0,
  );
  const daysPerBar = Math.ceil(days / CV_ANALYTICS_MAX_BARS);
  const bars: number[] = [];
  for (let index = 0; index < daily.length; index += daysPerBar) {
    bars.push(daily.slice(index, index + daysPerBar).reduce((sum, n) => sum + n, 0));
  }
  return {
    bars,
    daysPerBar,
    startTime,
    days,
    max: Math.max(0, ...bars),
    total: daily.reduce((sum, n) => sum + n, 0),
  };
}

/** UTC midnight of the first day in bar `index`. */
export function chartBarStart(chart: CvAnalyticsChart, index: number) {
  return chart.startTime + index * chart.daysPerBar * DAY_MS;
}

/** UTC midnight of the last day in bar `index`. */
export function chartBarEnd(chart: CvAnalyticsChart, index: number) {
  const lastDay = Math.min(chart.days - 1, (index + 1) * chart.daysPerBar - 1);
  return chart.startTime + lastDay * DAY_MS;
}

/** The three axis dates: first day, middle day, today. */
export function chartAxisTimes(chart: CvAnalyticsChart): [number, number, number] {
  return [
    chart.startTime,
    chart.startTime + Math.floor(chart.days / 2) * DAY_MS,
    chart.startTime + (chart.days - 1) * DAY_MS,
  ];
}

export function formatChartDate(time: number, locale: string) {
  try {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(time));
  } catch {
    return utcDayKey(time).slice(5);
  }
}

export function activityMetric(
  eventType: PublicProfileAnalyticsRecentActivity['eventType'],
): CvAnalyticsMetric | null {
  if (eventType === 'view') return 'views';
  if (eventType === 'download') return 'downloads';
  if (eventType === 'share') return 'shares';
  return null;
}

/**
 * The feed lists what the three metrics count, newest first. Contact and
 * block clicks stay in their own breakdowns rather than reading as "viewed".
 */
export function selectCvAnalyticsActivity(
  items: readonly PublicProfileAnalyticsRecentActivity[] | null | undefined,
) {
  return (items ?? [])
    .filter((item) => activityMetric(item.eventType) !== null)
    .map((item, index) => ({ item, index, at: Date.parse(item.createdAt) }))
    .sort((a, b) => {
      const left = Number.isFinite(a.at) ? a.at : -Infinity;
      const right = Number.isFinite(b.at) ? b.at : -Infinity;
      return right - left || a.index - b.index;
    })
    .map((entry) => entry.item);
}

export type CvAnalyticsAgeUnit = 'minute' | 'hour' | 'day';

/**
 * The feed's compact age ("12 min", "1 hr", "10 days", no "ago") as a count
 * and unit for the translated plural strings. Hermes' Intl unit formatting
 * does not pluralise ("4 day"), so the words come from i18n instead.
 */
export function activityAge(
  createdAt: string,
  now: number = Date.now(),
): { count: number; unit: CvAnalyticsAgeUnit } | null {
  const time = Date.parse(createdAt);
  if (!Number.isFinite(time)) return null;
  const minutes = Math.max(0, Math.floor((now - time) / 60_000));
  if (minutes < 60) return { count: Math.max(1, minutes), unit: 'minute' };
  if (minutes < 60 * 24) return { count: Math.floor(minutes / 60), unit: 'hour' };
  return { count: Math.floor(minutes / (60 * 24)), unit: 'day' };
}

/** The CV a filter or an activity row names, from the account's CV list. */
export interface CvAnalyticsCvOption {
  id: string;
  title: string;
}

export function cvOptionLabel(
  options: readonly CvAnalyticsCvOption[],
  cvId: string | null | undefined,
) {
  if (!cvId) return null;
  return options.find((option) => option.id === cvId)?.title?.trim() || null;
}

/**
 * What a viewer may see is the server's answer, not the client's guess (see
 * PublicProfileAnalyticsSummary.detail). With no answer yet, the entitlement
 * stands in so a paying user never flashes the upsell while loading.
 */
export function isCvAnalyticsPremium(
  data: Pick<PublicProfileAnalyticsSummary, 'detail'> | null | undefined,
  entitled: boolean,
) {
  return data ? data.detail === 'full' : entitled;
}

/** Identity of an activity source: who, not what they did. */
export interface CvAnalyticsActorKey {
  kind: 'person' | 'company' | 'anonymous';
  id: string | null;
}

/**
 * The quick look's avatar stack: the newest distinct sources, at most
 * `limit`. A member or company appears once however often they acted, and
 * all anonymous activity is one source.
 */
export function selectStackActors<T extends { actor: CvAnalyticsActorKey }>(
  rows: readonly T[],
  limit: number = CV_ANALYTICS_STACK_SIZE,
): T[] {
  const seen = new Set<string>();
  const picked: T[] = [];
  for (const row of rows) {
    const key = row.actor.kind === 'anonymous' ? 'anonymous' : `${row.actor.kind}:${row.actor.id ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    picked.push(row);
    if (picked.length >= limit) break;
  }
  return picked;
}

/** Up to two letters for an avatar without a picture ("Sara Ahmed" → "SA"). */
export function actorInitials(name: string | null | undefined) {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  const letters = words.length > 1 ? [words[0]!, words[words.length - 1]!] : [words[0]!];
  return letters.map((word) => Array.from(word)[0] ?? '').join('').toUpperCase();
}
